import { isEditableFinancialDraft } from '../../shared/accounting-draft-eligibility';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageDetailHeroComponent } from '../../../shared/page-layout/page-detail-hero.component';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { FinancePdfViewButtonComponent } from '../../finance-pdf-view-button.component';
import { AccountsService, FinancePdfStatus } from '../../accounts.service';
import { DebitNote } from '../debit-note.model';
import { AccountingDocumentAction, DocumentAuditEvent, GlPreview } from '../../accounting-lifecycle.model';
import { AccountingTabLabelComponent } from '../../shared/accounting-tab-label.component';
import { AccountingWorkflowActionsComponent } from '../../shared/accounting-workflow-actions.component';
import { AccountingAuditTimelineComponent } from '../../shared/accounting-audit-timeline.component';
import { AccountingGlPreviewPanelComponent } from '../../shared/accounting-gl-preview-panel.component';
import { AccountingAttachmentsPanelComponent } from '../../shared/accounting-attachments-panel.component';
import { ManualSourceBadgeComponent } from '../../shared/manual-source-badge.component';
import { AccountingTaxBreakdownPanelComponent } from '../../shared/accounting-tax-breakdown-panel.component';
import { ZatcaCompliancePanelComponent } from '../../shared/zatca-compliance-panel.component';
import { AccountingComplianceEvent } from '../../accounting-compliance.model';
import { finalize } from 'rxjs';
import { PendingActionTracker } from '../../../shared/state/pending-action-tracker';

@Component({
    selector: 'app-debit-note-detail',
    standalone: true,
    imports: [FinancePdfViewButtonComponent, CommonModule, ButtonModule, TableModule, TabsModule, TagModule, PageShellComponent, PageDetailHeroComponent, PageDataPanelComponent, PageMetricStripComponent, AccountingTabLabelComponent, AccountingWorkflowActionsComponent, AccountingAuditTimelineComponent, AccountingGlPreviewPanelComponent, AccountingAttachmentsPanelComponent, ManualSourceBadgeComponent, AccountingTaxBreakdownPanelComponent, ZatcaCompliancePanelComponent],
    templateUrl: './debit-note-detail.component.html',
    styleUrls: ['./debit-note-detail.component.css']
})
export class DebitNoteDetailComponent implements OnInit {
    readonly documentActions = new PendingActionTracker<string>();
    note?: DebitNote;
    pdfStatus?: FinancePdfStatus;
    pdfError = '';
    workflowError = '';
    auditEvents: DocumentAuditEvent[] = [];
    complianceEvents: AccountingComplianceEvent[] = [];
    glPreview?: GlPreview;
    retryingZatca = false;
    activeTab = 'overview';

    constructor(private route: ActivatedRoute, private router: Router, private accountsService: AccountsService) {}

    get metrics(): PageMetric[] {
        return [
            { label: 'Amount', value: this.money(this.note?.debitNoteAmount), hint: 'Debit note total', tone: 'info' },
            { label: 'Status', value: this.note?.documentStatus || 'UNKNOWN', hint: this.note?.postingStatus || 'Posting status' },
            { label: 'Original invoice', value: this.note?.originalInvoiceNumber || this.note?.invoice?.invoiceNumber || '-', hint: 'Source document' },
            { label: 'Lines', value: this.note?.debitNoteLines?.length || 0, hint: 'Adjustment lines' }
        ];
    }

    get canEditDraft(): boolean { return !!this.note?.debitNoteID && isEditableFinancialDraft(this.note); }

    editDraft(): void {
        if (this.canEditDraft) this.router.navigate(['/accounts/enterprise/receivables/debit-note/edit', this.note!.debitNoteID]);
    }

    ngOnInit(): void {
        const id = Number(this.route.snapshot.paramMap.get('id'));
        this.route.queryParamMap.subscribe(queryParams => {
            this.activeTab = this.resolveTab(queryParams.get('tab'));
        });
        this.accountsService.getDebitNoteById(id).subscribe(note => this.note = note);
        this.accountsService.financePdfStatus('debit-note', id).subscribe({ next: status => this.pdfStatus = status,
            error: () => this.pdfStatus = { status: 'MISSING', reprintAvailable: false } });
        this.accountsService.getDebitNoteAuditEvents(id).subscribe(events => this.auditEvents = events);
        this.accountsService.getComplianceEvents('DEBIT_NOTE', id).subscribe(events => this.complianceEvents = events);
        this.accountsService.getDebitNoteGlPreview(id).subscribe(preview => this.glPreview = preview);
    }

    onTabChange(value: string | number) {
        this.activeTab = this.resolveTab(String(value));
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: this.activeTab },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    private resolveTab(tabKey: string | null): string {
        const tabs = ['overview', 'lines', 'journal', 'attachments', 'compliance', 'workflow', 'activity'];
        const normalized = (tabKey || 'overview').toLowerCase();
        return tabs.includes(normalized) ? normalized : 'overview';
    }

    money(amount?: number) {
        return Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
    }

    workflow(action: AccountingDocumentAction, reason?: string) {
        if (!this.note?.debitNoteID || (action === 'cancel' && !isEditableFinancialDraft(this.note))) return;
        this.workflowError = '';
        this.accountsService.runDebitNoteWorkflow(this.note.debitNoteID, action, { source: 'PORTAL', reason }).subscribe({ next: () => {
            const id = this.note?.debitNoteID;
            if (id) {
                this.accountsService.getDebitNoteById(id).subscribe(note => this.note = note);
                this.accountsService.financePdfStatus('debit-note', id).subscribe(status => this.pdfStatus = status);
                this.accountsService.getDebitNoteAuditEvents(id).subscribe(events => this.auditEvents = events);
                this.accountsService.getDebitNoteGlPreview(id).subscribe(preview => this.glPreview = preview);
            }
        }, error: error => this.workflowError = error?.error?.message || 'Unable to update the debit note.' });
    }

    download() {
        if (!this.note?.debitNoteID || !this.documentActions.begin('download')) return;
        this.pdfError = '';
        if (this.pdfStatus?.status === 'MISSING') { this.pdfError = 'No stored issued PDF is available for this debit note.'; this.documentActions.end('download'); return; }
        this.accountsService.downloadDebitNoteDocument(this.note.debitNoteID)
            .pipe(finalize(() => this.documentActions.end('download')))
            .subscribe({ next: pdf => this.accountsService.downloadPdfDocument(pdf, 'debit-note', this.note?.debitNoteNumber, this.note?.debitNoteID, this.note?.postingStatus),
                error: () => this.pdfError = 'The debit note PDF is unavailable.' });
    }

    retryZatca() {
        if (!this.note?.debitNoteID) return;
        this.retryingZatca = true;
        this.accountsService.retryDebitNoteZatca(this.note.debitNoteID).subscribe({
            next: () => {
                this.retryingZatca = false;
                this.accountsService.getDebitNoteById(this.note!.debitNoteID!).subscribe(note => this.note = note);
                this.accountsService.getComplianceEvents('DEBIT_NOTE', this.note!.debitNoteID!).subscribe(events => this.complianceEvents = events);
            },
            error: error => { this.retryingZatca = false; this.workflowError = error?.error?.message || 'Unable to submit the debit note.'; }
        });
    }

    back() {
        this.router.navigate(['/accounts/enterprise/receivables/debit-note']);
    }
}
